import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Autocomplete,
  TextField,
  MenuItem,
  Button,
  Card as MuiCard,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import buybanner from "@assets/images/Real-Estate-Banner-01.jpg";
import CardComponent from "./card";
import { apiService } from "@services/api";
import { useFilter } from "@context/filterContext.jsx";
import ContactSection from "../Home/contactSection";
import {
  FaHome,
  FaMoneyBillWave,
  FaChartLine,
  FaHandshake,
  FaShieldAlt,
  FaUserCheck,
} from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import SEO from "../../../utils/SeoComponent";

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
}));

const PageSection1 = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(2),
  },
}));

const PageSection2 = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(2),
  },
}));

const HeroSection = styled(PageSection)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${buybanner})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "white",
  padding: theme.spacing(15, 0),
  textAlign: "center",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const SearchBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "10px",
  padding: theme.spacing(5),
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
  position: "relative",
  zIndex: 2,
  marginTop: "-100px",
  [theme.breakpoints.down("md")]: {
    marginTop: "-50px",
    padding: theme.spacing(3),
  },
}));

const SearchButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#C10E14",
  color: "#fff",
  padding: theme.spacing(1.5, 4),
  borderRadius: "50px",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
  "&:hover": {
    backgroundColor: "#A30B11",
  },
}));

const ClearButton = styled(Button)(({ theme }) => ({
  backgroundColor: "transparent",
  color: "black",
  border: "2px solid black",
  padding: theme.spacing(1.5, 4),
  borderRadius: "50px",
  fontWeight: 600,
  textTransform: "none",
  marginLeft: theme.spacing(2),
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(4),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  backgroundColor: "#f8f9fc",
  borderRadius: "50%",
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  color: "#C10E14",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(2),
  margin: theme.spacing(8, 0),
}));

const PaginationButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 3),
  borderRadius: "50px",
  fontWeight: 600,
  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
}));

const NoResultsCard = styled(MuiCard)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: "center",
  borderRadius: "15px",
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(6),
  backgroundColor: "#f8f9fc",
}));

const PageNumberDisplay = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1, 3),
  backgroundColor: "#f8f9fc",
  borderRadius: "50px",
  fontWeight: 600,
  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
}));

const BuyPage = () => {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [bedrooms, setBedrooms] = useState([]);
  const [prices, setPrices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 6;
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("query");

  const {
    selectedLocation,
    setSelectedLocation,
    selectedRoom,
    setSelectedRoom,
    selectedPrice,
    setSelectedPrice,
    filteredUsers,
    setFilteredUsers,
    isFilterApplied,
    setIsFilterApplied,
  } = useFilter();

  // Calculate current users (paginated)
  const getCurrentUsers = () => {
    const dataToUse = isFilterApplied ? filteredUsers : users;
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    return dataToUse.slice(indexOfFirstUser, indexOfLastUser);
  };

  // Get total pages
  const getTotalPages = () => {
    const dataToUse = isFilterApplied ? filteredUsers : users;
    return Math.ceil(dataToUse.length / usersPerPage);
  };

  // Handle next page
  const handleNext = () => {
    if (currentPage < getTotalPages()) {
      setCurrentPage((prev) => prev + 1);
      scrollToPropertyListings();
    }
  };

  // Handle previous page
  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      scrollToPropertyListings();
    }
  };

  // Scroll to property listings
  const scrollToPropertyListings = () => {
    const element = document.getElementById("property-listings");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Reset to first page whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [isFilterApplied, filteredUsers]);

  const features = [
    {
      icon: <FaHome />,
      title: "Premium Properties",
      description:
        "Access to exclusive high-end properties in Dubai's most sought-after locations.",
    },
    {
      icon: <FaChartLine />,
      title: "Investment Opportunities",
      description:
        "Identify properties with strong ROI potential and long-term value growth.",
    },
    {
      icon: <FaMoneyBillWave />,
      title: "Competitive Pricing",
      description:
        "Transparent pricing structure with no hidden fees or commissions.",
    },
    {
      icon: <FaShieldAlt />,
      title: "Secure Transactions",
      description:
        "All property transactions are legally verified and compliant with Dubai real estate laws.",
    },
    {
      icon: <FaUserCheck />,
      title: "Verified Listings",
      description:
        "Every property is personally inspected and verified by our team of experts.",
    },
    {
      icon: <FaHandshake />,
      title: "End-to-End Support",
      description:
        "Full assistance from property viewing to final key handover and beyond.",
    },
  ];

  const handleClearFilters = () => {
    setSelectedLocation("");
    setSelectedRoom("");
    setSelectedPrice("");
    setFilteredUsers([]);
    setIsFilterApplied(false);
    setCurrentPage(1);
  };

  // Helper function to check if any filters are applied
  const hasFiltersApplied = () => {
    return selectedLocation || selectedRoom || selectedPrice;
  };

  const handleSearch = async () => {
    // Check if any filters are actually selected
    if (!hasFiltersApplied()) {
      console.log("No filters selected");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("location", selectedLocation);
      formData.append("room", selectedRoom);
      formData.append("price", selectedPrice);

      const response = await apiService.filterBuyProperties(formData);
      const filteredData = response.data.data;
      
      setFilteredUsers(filteredData);
      setIsFilterApplied(true);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
      // Handle error case - don't set isFilterApplied to true
      setFilteredUsers([]);
      setIsFilterApplied(false);
    }
  };

  // Separate function to handle search query from URL
  const handleSearchQuery = async (query) => {
    if (!query) return;

    try {
      const formData = new FormData();
      formData.append("title", query); // Search by title/query instead of location
      
      const response = await apiService.filterBuyProperties(formData);
      const filteredData = response.data.data;
      
      setFilteredUsers(filteredData);
      setIsFilterApplied(true);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching search results:", error);
      setFilteredUsers([]);
      setIsFilterApplied(false);
    }
  };

  useEffect(() => {
    apiService
      .getBuyProperties()
      .then((response) => {
        const data = response.data.data;

        const locationList = data
          .map((item) => item.location)
          .filter(
            (value, index, self) => value && self.indexOf(value) === index
          )
          .map((loc) => ({ label: loc }));

        const bedroomList = data
          .map((item) => item.room)
          .filter((v, i, a) => v && a.indexOf(v) === i)
          .map((room) => ({ label: `${room} Bedroom${room > 1 ? "s" : ""}` }));

        const priceList = data
          .map((item) => item.price)
          .filter((v, i, a) => v && a.indexOf(v) === i)
          .map((price) => ({ label: `AED ${price}` }));

        setLocations(locationList);
        setBedrooms(bedroomList);
        setPrices(priceList);
        setUsers(response.data.data);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
  }, []);

  // Handle search query from URL parameters
  useEffect(() => {
    if (searchQuery) {
      handleSearchQuery(searchQuery);
    }
  }, [searchQuery]);

  return (
    <>
      <SEO slug={"buy"} />
      <Box className="buy-page">
        {/* Hero Section */}
        <HeroSection className="hero-section">
          <Container
            maxWidth="md"
            sx={{
              mt: { xs: 6, md: 14 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                fontWeight={700}
              >
                Properties for Sale in Dubai
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Find your dream property investment with our exclusive listings
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const element = document.getElementById("property-listings");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                sx={{
                  borderRadius: "50px",
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
                  backgroundColor: "#C10E14",
                  "&:hover": {
                    backgroundColor: "#A30B11",
                  },
                }}
              >
                Browse Properties
              </Button>
            </motion.div>
          </Container>
        </HeroSection>

        {/* Search Section */}
        <Container>
          <SearchBox className="search-section">
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <Autocomplete
                  disablePortal
                  options={locations}
                  getOptionLabel={(option) => option.label}
                  value={selectedLocation ? { label: selectedLocation } : null}
                  onChange={(event, newValue) =>
                    setSelectedLocation(newValue?.label || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Location"
                      fullWidth
                      sx={{
                        "& .MuiInputLabel-root": {
                          color: "black",
                          "&.Mui-focused": {
                            color: "#C10E14",
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          color: "black",
                          "& fieldset": {
                            borderColor: "black",
                          },
                          "&:hover fieldset": {
                            borderColor: "black",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#C10E14",
                          },
                        },
                        "& .MuiSvgIcon-root": {
                          color: "black",
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Bedroom"
                  value={selectedRoom}
                  onChange={(e) => setSelectedRoom(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "black",
                      "&.Mui-focused": {
                        color: "#C10E14",
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      color: "black",
                      "& fieldset": {
                        borderColor: "black",
                      },
                      "&:hover fieldset": {
                        borderColor: "black",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C10E14",
                      },
                    },
                    "& .MuiSvgIcon-root": {
                      color: "black",
                    },
                  }}
                >
                  {bedrooms.map((room, index) => (
                    <MenuItem key={index} value={room.label}>
                      {room.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Price"
                  value={selectedPrice}
                  onChange={(e) => setSelectedPrice(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "black",
                      "&.Mui-focused": {
                        color: "#C10E14",
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      color: "black",
                      "& fieldset": {
                        borderColor: "black",
                      },
                      "&:hover fieldset": {
                        borderColor: "black",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C10E14",
                      },
                    },
                    "& .MuiSvgIcon-root": {
                      color: "black",
                    },
                  }}
                >
                  {prices.map((price, index) => (
                    <MenuItem key={index} value={price.label}>
                      {price.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: "flex" }}>
                  <SearchButton 
                    fullWidth 
                    onClick={handleSearch}
                    disabled={!hasFiltersApplied()}
                  >
                    Search
                  </SearchButton>
                  {isFilterApplied && (
                    <ClearButton onClick={handleClearFilters}>
                      Clear
                    </ClearButton>
                  )}
                </Box>
              </Grid>
            </Grid>
          </SearchBox>
        </Container>

        {/* Features Section */}
        <PageSection1 className="features-section" sx={{ bgcolor: "#f8f9fc" }}>
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Why Buy with DESCO?
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: "700px", mx: "auto" }}
              >
                We make finding and purchasing your dream property in Dubai
                simple, secure, and rewarding
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <FeatureItem>
                      <IconWrapper>{feature.icon}</IconWrapper>
                      <Box>
                        <Typography
                          variant="h6"
                          component="h3"
                          fontWeight={600}
                          gutterBottom
                        >
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </Box>
                    </FeatureItem>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </PageSection1>

        {/* Properties Listing Section */}
        <PageSection2 id="property-listings" className="properties-section">
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Available Properties for Sale
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: "700px", mx: "auto" }}
              >
                {isFilterApplied
                  ? `Showing ${getCurrentUsers().length} of ${filteredUsers.length} filtered properties`
                  : `Browse our exclusive selection of properties for sale across Dubai`}
              </Typography>
            </Box>

            {isFilterApplied ? (
              filteredUsers && filteredUsers.length > 0 ? (
                <>
                  <CardComponent data={getCurrentUsers()} pageType={"buy"} />
                  <PaginationContainer>
                    <PaginationButton
                      variant="outlined"
                      onClick={handlePrevious}
                      disabled={currentPage === 1}
                      sx={{
                        borderColor: "#C10E14",
                        color: "#C10E14",
                        "&:hover": {
                          borderColor: "#A30B11",
                          backgroundColor: "rgba(193, 14, 20, 0.04)",
                        },
                      }}
                    >
                      Previous
                    </PaginationButton>

                    <PageNumberDisplay>
                      Page {currentPage} of {getTotalPages()}
                    </PageNumberDisplay>

                    <PaginationButton
                      variant="contained"
                      onClick={handleNext}
                      disabled={currentPage === getTotalPages()}
                      sx={{
                        backgroundColor: "#C10E14",
                        "&:hover": {
                          backgroundColor: "#A30B11",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#f1c1c3",
                        },
                      }}
                    >
                      Next
                    </PaginationButton>
                  </PaginationContainer>
                </>
              ) : (
                <Container maxWidth="md">
                  <NoResultsCard>
                    <Typography variant="h5" component="h3" gutterBottom>
                      😕 No Results Found
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                    >
                      We couldn't find any properties matching your selected
                      filters.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleClearFilters}
                      sx={{
                        borderRadius: "50px",
                        py: 1,
                        px: 3,
                        mt: 2,
                        backgroundColor: "#C10E14",
                        "&:hover": {
                          backgroundColor: "#A30B11",
                        },
                      }}
                    >
                      Clear All Filters
                    </Button>
                  </NoResultsCard>
                </Container>
              )
            ) : users.length > 0 ? (
              <>
                <CardComponent data={getCurrentUsers()} pageType={"buy"} />
                <PaginationContainer>
                  <PaginationButton
                    variant="outlined"
                    onClick={handlePrevious}
                    disabled={currentPage === 1}
                    sx={{
                      borderColor: "#C10E14",
                      color: "#C10E14",
                      "&:hover": {
                        borderColor: "#A30B11",
                        backgroundColor: "rgba(193, 14, 20, 0.04)",
                      },
                    }}
                  >
                    Previous
                  </PaginationButton>

                  <PageNumberDisplay>
                    Page {currentPage} of {getTotalPages()}
                  </PageNumberDisplay>

                  <PaginationButton
                    variant="contained"
                    onClick={handleNext}
                    disabled={currentPage === getTotalPages()}
                    sx={{
                      backgroundColor: "#C10E14",
                      "&:hover": {
                        backgroundColor: "#A30B11",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#f1c1c3",
                      },
                    }}
                  >
                    Next
                  </PaginationButton>
                </PaginationContainer>
              </>
            ) : (
              <Typography variant="body1" align="center">
                Loading properties...
              </Typography>
            )}
          </Container>
        </PageSection2>

        {/* Contact Section */}
        <Box
          sx={{
            padding: "80px 0px",
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${buybanner})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <ContactSection />
        </Box>
      </Box>
    </>
  );
};

export default BuyPage;